import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { apiGet, apiPost, apiPut, apiDelete } from "helpers/apiService";
import SafeField from "helpers/SafeField";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";

const LocalizationResource = () => {
  const [list, setList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchList();
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  /* ================= FETCH ================= */

  const fetchList = () => {
    apiGet("LocalizationResource/allfull")
      .then(res => setList(res.data))
      .catch(() => toast.error("Failed to load localization resources"));
  };

  /* ================= CREATE ================= */

  const handleCreate = (values, { setSubmitting, resetForm }) => {
    toast.loading("Saving...");

    const payload = {
      localizationResourceKey: values.localizationResourceKey,
      localizationResourceCategory: values.localizationResourceCategory,
      localizationResourceCulture: values.localizationResourceCulture,
      localizationResourceValue: values.localizationResourceValue,
    };

    apiPost("LocalizationResource", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Saved successfully");
        fetchList();
        resetForm();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Save failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= UPDATE ================= */

  const handleUpdate = (values, { setSubmitting }) => {
    toast.loading("Updating...");

    const payload = {
        id: values.id,
      localizationResourceId: values.localizationResourceId,
      localizationResourceKey: values.localizationResourceKey,
      localizationResourceCategory: values.localizationResourceCategory,
      localizationResourceCulture: values.localizationResourceCulture,
      localizationResourceValue: values.localizationResourceValue,
    };

    apiPut("LocalizationResource", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Updated successfully");
        fetchList();
        toggle();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Update failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= DELETE ================= */

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(result => {
      if (!result.isConfirmed) return;

      apiDelete(`LocalizationResource/${id}`)
        .then(() => {
          toast.success("Deleted successfully");
          fetchList();
        })
        .catch(() => toast.error("Delete failed"));
    });
  };

  const openEdit = item => {
    setSelected(item);
    toggle();
  };

  /* ================= VALIDATION ================= */

  const validationSchema = Yup.object({
    localizationResourceKey: Yup.string().required("Key is required"),
    localizationResourceCategory: Yup.string().required("Category is required"),
    localizationResourceCulture: Yup.string().required("Culture is required"),
    localizationResourceValue: Yup.string().required("Value is required"),
  });

  return (
    <Container fluid className="page-content">
      <ToastContainer autoClose={2000} />
        <Row className="mb20">
            <Col xl="12">
                <Breadcrumbs title={"General Settings"} breadcrumbItem={"Localization Resource"} />
            </Col>
        </Row>
      <Row>
        <Col xl="12">
          {/* ===== ADD FORM ===== */}
          <Card>
            <CardBody>
              

              <Formik
                initialValues={{
                  localizationResourceKey: "",
                  localizationResourceCategory: "",
                  localizationResourceCulture: "",
                  localizationResourceValue: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleCreate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <Row>
                      <Col md="3">
                        <FormGroup>
                          <Label>Key *</Label>
                          <SafeField
                            name="localizationResourceKey"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.localizationResourceKey &&
                              touched.localizationResourceKey
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name="localizationResourceKey"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup>
                          <Label>Category *</Label>
                          <SafeField
                            name="localizationResourceCategory"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.localizationResourceCategory &&
                              touched.localizationResourceCategory
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="2">
                        <FormGroup>
                          <Label>Culture *</Label>
                          <SafeField
                            name="localizationResourceCulture"
                            placeholder="en-US"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.localizationResourceCulture &&
                              touched.localizationResourceCulture
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup>
                          <Label>Value *</Label>
                          <SafeField
                            name="localizationResourceValue"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.localizationResourceValue &&
                              touched.localizationResourceValue
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="1" className="">
                        <Button type="submit" color="primary" className="mt-4">
                          Save
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </CardBody>
          </Card>

          {/* ===== LIST ===== */}
          <Card>
            <CardBody>
              <h5>Localization List</h5>

              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Key</th>
                      <th>Category</th>
                      <th>Culture</th>
                      <th>Value</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item, i) => (
                      <tr key={item.localizationResourceId || i}>
                        <td>{i + 1}</td>
                        <td>{item.localizationResourceKey}</td>
                        <td>{item.localizationResourceCategory}</td>
                        <td>{item.localizationResourceCulture}</td>
                        <td>{item.localizationResourceValue}</td>
                        <td>
                        <ActionEdit onClick={() => openEdit(item)} />{" "}
                        <ActionDelete onClick={() => handleDelete(item.id)} />
                         
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ===== EDIT OFFCANVAS ===== */}
      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
        <OffcanvasHeader toggle={toggle}>Update Resource</OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={selected}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
          >
            {({ handleChange }) => (
              <Form>
                <FormGroup>
                  <Label>Key *</Label>
                  <SafeField
                    name="localizationResourceKey"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Category *</Label>
                  <SafeField
                    name="localizationResourceCategory"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Culture *</Label>
                  <SafeField
                    name="localizationResourceCulture"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Value *</Label>
                  <SafeField
                    name="localizationResourceValue"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <Button type="submit" color="primary">
                  Update
                </Button>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
};

export default LocalizationResource;
